'use strict';

const SESSION_FREQUENCY = 'once_per_session';
const USER_FREQUENCY = 'once_per_user';
const DAILY_LIMITS = {
    once_per_day: 1,
    twice_per_day: 2,
    three_times_per_day: 3
};
const POPUP_STATE = 'pdPopupState';
const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
].join(',');

function getDateKey() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');

    return year + '-' + month + '-' + day;
}

function getStorageKey($popup, suffix) {
    const popupId = String($popup.data('popup-id') || 'unknown');
    const keyPrefixes = {
        session: 'pdPopupWasShown:',
        user: 'pdPopupUser:',
        daily: 'pdPopupDaily:'
    };

    return keyPrefixes[suffix] + popupId;
}

function clearInactiveFrequencyState($popup, frequency) {
    let active = 'daily';
    if (frequency === SESSION_FREQUENCY) active = 'session';
    if (frequency === USER_FREQUENCY) active = 'user';

    if (active !== 'session') window.sessionStorage.removeItem(getStorageKey($popup, 'session'));
    if (active !== 'user') window.localStorage.removeItem(getStorageKey($popup, 'user'));
    if (active !== 'daily') window.localStorage.removeItem(getStorageKey($popup, 'daily'));
}

function getDailyState($popup) {
    const today = getDateKey();
    const rawValue = window.localStorage.getItem(getStorageKey($popup, 'daily'));

    if (rawValue) {
        try {
            const state = JSON.parse(rawValue);

            if (state && state.date === today) {
                return {
                    date: today,
                    count: Number(state.count || 0)
                };
            }
        } catch (e) {
            // Use the default state for invalid storage values.
        }
    }

    return {
        date: today,
        count: 0
    };
}

function canShow($popup) {
    const frequency = $popup.data('popup-frequency') || SESSION_FREQUENCY;

    clearInactiveFrequencyState($popup, frequency);

    if (frequency === SESSION_FREQUENCY) {
        return window.sessionStorage.getItem(getStorageKey($popup, 'session')) !== 'true';
    }

    if (frequency === USER_FREQUENCY) {
        return window.localStorage.getItem(getStorageKey($popup, 'user')) !== 'true';
    }

    const limit = DAILY_LIMITS[frequency] || 1;
    const state = getDailyState($popup);

    return Number(state.count || 0) < limit;
}

function recordShown($popup) {
    const frequency = $popup.data('popup-frequency') || SESSION_FREQUENCY;

    if (frequency === SESSION_FREQUENCY) {
        window.sessionStorage.setItem(getStorageKey($popup, 'session'), 'true');
        return;
    }

    if (frequency === USER_FREQUENCY) {
        window.localStorage.setItem(getStorageKey($popup, 'user'), 'true');
        return;
    }

    const key = getStorageKey($popup, 'daily');
    const state = getDailyState($popup);

    window.localStorage.setItem(key, JSON.stringify({ date: state.date, count: state.count + 1 }));
}

function isRestorableFocusElement(element) {
    return Boolean(
        element
        && element !== document.body
        && element !== document.documentElement
        && document.documentElement.contains(element)
        && !element.disabled
        && element.getAttribute('aria-hidden') !== 'true'
        && typeof element.focus === 'function'
    );
}

function focusElement(element) {
    try {
        element.focus({ preventScroll: true });
    } catch (e) {
        element.focus();
    }
}

function restoreFocus(element) {
    if (isRestorableFocusElement(element)) {
        focusElement(element);
        return;
    }

    const mainContent = document.getElementById('maincontent');

    if (!mainContent) return;

    const hadTabindex = mainContent.hasAttribute('tabindex');

    if (!hadTabindex) mainContent.setAttribute('tabindex', '-1');
    focusElement(mainContent);

    if (!hadTabindex) {
        window.setTimeout(function () {
            if (document.activeElement === mainContent) mainContent.removeAttribute('tabindex');
        }, 0);
    }
}

function getFocusableElements($popup) {
    return $popup
        .find(FOCUSABLE_SELECTOR)
        .filter(':visible')
        .toArray();
}

function trapPopupFocus(event, $popup) {
    if (event.key !== 'Tab' && event.keyCode !== 9) return;

    const focusableElements = getFocusableElements($popup);

    if (!focusableElements.length) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;

    if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        focusElement(lastElement);
        return;
    }

    if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        focusElement(firstElement);
    }
}

function cleanupPopupFocus($popup) {
    const $closeButton = $popup.find('.b-global-popup__close');

    $popup.off('keydown.pdPopupFocusTrap');
    $closeButton.off('blur.pdPopupInitialFocus').removeClass('m-initial-focus');
}

function setupPopupFocus($popup) {
    const $closeButton = $popup.find('.b-global-popup__close');
    const closeButton = $closeButton.get(0);

    cleanupPopupFocus($popup);
    $popup.on('keydown.pdPopupFocusTrap', function (event) {
        trapPopupFocus(event, $popup);
    });

    $closeButton.addClass('m-initial-focus').one('blur.pdPopupInitialFocus', function () {
        $(this).removeClass('m-initial-focus');
    });

    if (closeButton) focusElement(closeButton);
}

/**
 * Activates media sources for the popup selected for display.
 *
 * @param {jQuery} $popup Popup element.
 * @returns {void}
 */
function hydratePopupMedia($popup) {
    const $picture = $popup.find('.b-global-popup__media picture').first();

    if (!$picture.length || $picture.data('pdPopupMediaHydrated')) return;

    $picture.find('[data-pd-popup-srcset]').each(function () {
        const $source = $(this);
        $source.attr('srcset', $source.attr('data-pd-popup-srcset'));
    });

    const $image = $picture.find('[data-pd-popup-src]').first();

    if ($image.length) {
        $image.attr('src', $image.attr('data-pd-popup-src'));
    }

    $picture.data('pdPopupMediaHydrated', true);
}

function getCandidatePopup() {
    const $pagePopup = $('.js-global-popup-scope-page .js-global-popup').first();
    if ($pagePopup.length) {
        return $pagePopup;
    }

    return $('.js-global-popup-scope-global .js-global-popup').first();
}

function pushClickEvent($popup, anchor) {
    const $anchor = $(anchor);
    const clickElement = $anchor.data('popup-click-element') || 'Text';

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'click_popup',
        popup_id: String($popup.data('popup-id') || ''),
        target_url: anchor.href || $anchor.attr('href') || '',
        click_element: clickElement
    });
}

function bindTracking($popup) {
    $popup.off('click.pdPopupTracking').on('click.pdPopupTracking', 'a[href]', function () {
        pushClickEvent($popup, this);
    });
}

function showWhenAvailable($popup) {
    if (!canShow($popup) || $popup.data(POPUP_STATE) || $popup.hasClass('show')) return;

    $popup.data(POPUP_STATE, 'queued');

    hydratePopupMedia($popup);

    const showPopup = function () {
        if ($popup.data(POPUP_STATE) !== 'queued') return;

        const $otherOpenModal = $('.modal.show').not($popup).first();

        if ($otherOpenModal.length) {
            $(document).one('hidden.bs.modal.pdPopupQueue', showPopup);
            return;
        }

        $popup.data(POPUP_STATE, 'showing');
        const restoreFocusElement = document.activeElement;

        $popup.one('show.bs.modal.pdPopup', function () {
            $('body').addClass('global-popup-open');
        });

        $popup.one('shown.bs.modal.pdPopup', function () {
            recordShown($popup);
            setupPopupFocus($popup);
        });

        $popup.one('hide.bs.modal.pdPopup', function () {
            if ($popup[0].contains(document.activeElement) && typeof document.activeElement.blur === 'function') {
                document.activeElement.blur();
            }
        });

        $popup.one('hidden.bs.modal.pdPopup', function () {
            cleanupPopupFocus($popup);
            $('body').removeClass('global-popup-open');
            $popup.removeData(POPUP_STATE);
            restoreFocus(restoreFocusElement);
        });

        $popup.modal({
            backdrop: true,
            keyboard: true,
            show: true
        });
    };

    showPopup();
}

module.exports = {
    init: function () {
        const $popup = getCandidatePopup();

        if (!$popup.length) return;

        bindTracking($popup);
        showWhenAvailable($popup);
    }
};
